<template>
    <div class="quick-payment-method-selector styled-scrollbar scrollable" :class="paymentMethodClasses">
        <button
            type="button"
            v-for="quickPaymentMethod in quickPaymentMethods"
            :key="quickPaymentMethod.id"
            :disabled="quickPaymentMethod.type !== 'OpenCashDrawer' && disabled"
            :data-method-name="quickPaymentMethod.name"
            class="method"
            @click="onClick(quickPaymentMethod)"
            @touchstart="onTouchStart"
            @touchend="(e) => onTouchEnd(e, quickPaymentMethod)"
        >
            <div
                v-if="quickPaymentMethod.imagePath"
                class="icon"
                :style="{ 'background-image': `url(${quickPaymentMethod.imagePath})` }"
            ></div>
            <i v-else class="fal fa-cash-register"></i>
        </button>
    </div>
</template>

<script setup lang="ts">
    import { useTapRecognizer } from "@/components/composables/useTapRecognizer";
    import type { IPaymentMethod } from "@/data/model/Customer";
    import { computed } from "vue";

    const emit = defineEmits(["onSelected"]);
    const props = defineProps<{
        quickPaymentMethods: IPaymentMethod[];
        disabled: boolean;
    }>();

    const paymentMethodClasses = computed(() => {
        if (props.quickPaymentMethods == null) return;

        return {
            "methods-1": props.quickPaymentMethods.length == 1,
            "methods-2": props.quickPaymentMethods.length == 2,
            "methods-3": props.quickPaymentMethods.length >= 3,
        };
    });

    // --- Touch handling ---
    const { onTouchStart, onTouchEnd } = useTapRecognizer<IPaymentMethod>({
        onTap: (method) => selectMethod(method),
        isDisabled: () => props.disabled,
    });

    // --- Desktop fallback ---
    function onClick(method: IPaymentMethod) {
        console.log("onClick", method);
        selectMethod(method);
    }

    function selectMethod(method: IPaymentMethod) {
        console.log("selectMethod", method);
        if (method.type !== "OpenCashDrawer" && props.disabled) return;

        emit("onSelected", method);
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";

    .quick-payment-method-selector {
        display: flex;
        padding: 16px 0;
        white-space: nowrap;
        overflow-x: auto;
        overflow-y: hidden;

        &.methods-1 {
            min-width: 68px;
        }

        &.methods-2 {
            min-width: 136px;
        }

        &.methods-3 {
            min-width: 204px;
        }

        @media (max-width: 425px) {
            &.methods-1 {
                min-width: 68px !important;
            }

            &.methods-2 {
                min-width: 68px !important;
            }

            &.methods-3 {
                min-width: 68px !important;
            }
        }

        @media (max-width: 1000px) {
            &.methods-1 {
                min-width: 68px;
            }

            &.methods-2 {
                min-width: 136px;
            }

            &.methods-3 {
                min-width: 136px;
            }
        }

        > .method {
            cursor: pointer;
            margin: 2px 16px 2px 0;
            width: 72px;
            min-width: 72px;
            height: 43px;
            box-sizing: border-box;
            border: 1px solid #dedede;
            border-radius: 5px;
            overflow: hidden;
            background-color: white;

            &:disabled {
                background-color: colors.$lightGrey;
                cursor: default;
            }

            &:last-child {
                margin-right: 0;
            }

            > .icon {
                width: 40px;
                height: 26px;
                background-size: contain;
                background-repeat: no-repeat;
                background-position: center center;
                margin: 7px auto;
            }

            > .fa-cash-register {
                position: absolute;
                left: 19px;
                top: 5px;
                font-size: 28px;
                color: colors.$fontColor;
            }
        }
    }
</style>
